import {BlobId, DeviceSlotId, ProjectId, UserId} from '../types/ids';
import {Observable, of, ReplaySubject} from 'rxjs';
import {ProjectInfo} from '../types/projectInfo';
import {Injectable} from '@angular/core';
import {ProjectApi} from '@webmate-web-core/shared/api/rest/project.restapi';
import {UserService} from '@webmate-web-core/shared/services/user.service';
import {AdminApi} from '@webmate-web-core/shared/api/rest/administration.restapi';
import {map, mergeMap, take, tap} from 'rxjs/operators';

import {Project} from '@webmate-web-core/shared/types/interfaces/project.interface';
import {EventsService} from '@webmate-web-core/shared/services/events/events.service';
import {DateTime} from '@webmate-web-core/shared/types/base-types';
import {DeviceSlotService} from '@webmate-web-core/shared/services/device/device-slot.service';

export interface ProjectNotes {
    body: any;
    creationTime: DateTime;
    creator: UserId | undefined;
}

@Injectable({
    providedIn: 'root',
})
export class ProjectService {
    private projectsById: { [s: string /* ProjectId*/]: ReplaySubject<Project> } = {}; //actual project Cache
    private projectInfosForCustomer: ReplaySubject<ProjectInfo[]>; //cache for lightweight ProjectInfos for the current CUSTOMER. This is an administrative UseCase!
    private projectInfosForCurrentUser: ReplaySubject<ProjectInfo[]>;

    constructor(private projectApi: ProjectApi, private userService: UserService, private adminApi: AdminApi,
                private eventService: EventsService, private slotService: DeviceSlotService) {
    }

    private addProjectToCache(p: Project) {
        if (!this.projectsById[p.id]) {
            this.projectsById[p.id] = new ReplaySubject(1);
        }
        this.projectsById[p.id].next(p);
    }

    private fetchProject(id: ProjectId): Observable<any> {
        return this.projectApi.getProject(id).pipe(map((next) => {
           this.addProjectToCache(next);
        }), take(1));
    }

    private fetchProjectInfosForCustomer(): Observable<ProjectInfo[]> {
        return this.adminApi.getProjectsOfCustomerMinimized()
            .pipe(tap((value) => {
                if (!this.projectInfosForCustomer) {
                    this.projectInfosForCustomer = new ReplaySubject(1);
                }
                this.projectInfosForCustomer.next(value);
            }));
    }

    fetchProjectInfosForCurrentUser(): Observable<ProjectInfo[]> {
        return this.projectApi.getProjectsForCurrentUser()
            .pipe(tap((value) => {
                if (!this.projectInfosForCurrentUser) {
                    this.projectInfosForCurrentUser = new ReplaySubject(1);
                }
                this.projectInfosForCurrentUser.next(value);
            }));
    }

    private fetchAllProjectInfos(): Observable<any> {
        return ((this.projectInfosForCustomer) ? this.fetchProjectInfosForCustomer() : of({}))
            .pipe(mergeMap(() => (this.projectInfosForCurrentUser) ? this.fetchProjectInfosForCurrentUser() : of({})));
    }

    /** Obtains a single project, may query it from the BackEnd in select cases...
     *  If the current user is an Admin, he can query all projects of the current customer, otherwise he can only query his own projects using this function */
    getProjectById$(projectId: ProjectId): Observable<Project> {
        if (!projectId) throw new ReferenceError('\'getProjectById$\' called with undefined');
        if (!this.projectsById[projectId]) {
            return this.fetchProject(projectId).pipe(mergeMap(_ => this.projectsById[projectId].asObservable()));
        }
        return this.projectsById[projectId].asObservable();
    }

    /**
     * This is an admin use-case. Gets a lightweight representation of ALL Projects of the current Customer, regardless of Access-Rights
     */
    getProjectInfosForCustomer(): Observable<ProjectInfo[]> {
        if (this.projectInfosForCustomer) {
            return this.projectInfosForCustomer.asObservable();
        } else {
            return this.fetchProjectInfosForCustomer().pipe(mergeMap(() => this.projectInfosForCustomer.asObservable()));
        }
    }

    getProjectInfosForCurrentUser(): Observable<ProjectInfo[]> {
        if (this.projectInfosForCurrentUser) {
            return this.projectInfosForCurrentUser.asObservable();
        } else {
            return this.fetchProjectInfosForCurrentUser().pipe(mergeMap(() => this.projectInfosForCurrentUser.asObservable()));
        }
    }

    /** updates the properties of a Project. Most properties are must-haves but the logo is optional and can be removed by passing 'null' */
    updateProjectProperties(projectId: ProjectId, newName: string, newDescription: string, newLogo: BlobId | null): Observable<any> {
        return this.adminApi.updateProjectProperties(projectId, newName, newDescription, newLogo).pipe(mergeMap((next) => {
            if (this.projectsById[projectId]) {
                return this.fetchProject(projectId);
            } else {
                return of({});
            }
        }), mergeMap(() => this.fetchAllProjectInfos()));
    }

    updateProjectNotes(projectId: ProjectId, newNotes: ProjectNotes[]): Observable<any> {
        return this.adminApi.updateProjectNotes(projectId, newNotes).pipe(mergeMap((next) => {
            if (this.projectsById[projectId]) {
                return this.fetchProject(projectId);
            } else {
                return of({});
            }
        }), mergeMap(() => this.fetchAllProjectInfos()));
    }

    deleteProjectById(projectId: ProjectId): Observable<any> {
        return this.adminApi.deleteProject(projectId).pipe(mergeMap(() => {
            this.eventService.stopPullingEventsForProject(projectId);
            return this.userService.refreshCaches();
        }), mergeMap(() => this.fetchAllProjectInfos()));
    }

    createNewProject(name: string, description: string, slots: DeviceSlotId[], roleMapping?: any[], parent?: ProjectId): Observable<ProjectId> {
        return this.adminApi.createNewProject(name, description, slots, roleMapping, parent).pipe(
            mergeMap(pid => this.fetchAllProjectInfos().pipe(map(() => pid))),
            mergeMap(pid => this.userService.refreshCaches().pipe(map(() => pid))),
            mergeMap(pid => this.slotService.refreshAdminSlotCaches().pipe(map(() => pid)))
        );
    }

    setRedeploymentTime(projectId: ProjectId, hour: number, minute: number): Observable<any> {
        return this.adminApi.setRedeploymentTime(projectId, hour, minute).pipe(mergeMap((next) => this.fetchProject(projectId)),
            mergeMap(() => this.fetchAllProjectInfos()));
    }

}
